import { useCallback, useEffect } from 'react';
import { GrantStatusEnum } from '../../enums/GrantStatusEnum';
import type { GetGrantsResponse } from '../../interfaces/GrantApiService';
import { authService } from '../../services/authService';
import { GrantService } from '../../services/grantApiService';
import { useAppStore } from '../../store/store';
import { hasFileAccessScope } from '../../utils/jwt';

// Instantiate the GrantService once for API calls related to pending file shares.
const grantService = new GrantService();

/**
 * Custom React hook that manages the fetching of pending file share requests for the header.
 * Provides the pending grants list and a refresh function.
 * These grants are stored separately in Zustand to avoid conflicts with the main grants list.
 */
function usePendingFileShareRequests() {
  // State: List of pending grants from global store
  const pendingGrants = useAppStore((state) => state.pendingGrants);
  const setPendingGrants = useAppStore((state) => state.setPendingGrants);
  // const updatePendingGrants = useAppStore((state) => state.updatePendingGrants);
  const removePendingGrant = useAppStore((state) => state.removePendingGrant);
  const authenticatedUser = useAppStore((state) => state.authenticatedUser);

  /**
   * Fetches pending grants from the server and updates the store.
   * This is used to refresh the pending grants list in the header.
   */
  const fetchPendingGrants = useCallback(async () => {
    // Don't fetch if user is not authenticated, or file-scoped OTP / anticapture
    // (grants API not on allow-list → 403 DENIED).
    if (!authenticatedUser || hasFileAccessScope(authService.getToken())) {
      return;
    }

    try {
      const response: GetGrantsResponse = await grantService.getGrants({
        offset: 0,
        count: 50, // Fetch up to 50 pending grants for the header
        status: GrantStatusEnum.Pending,
      });

      setPendingGrants(response.grants);
    } catch (error) {
      console.error('Failed to fetch pending grants', error);
    }
  }, [setPendingGrants, authenticatedUser]);

  /**
   * Refreshes the pending grants list.
   * This should be called after processing a grant (approve/reject).
   */
  const refreshPendingGrants = useCallback(() => {
    fetchPendingGrants();
  }, [fetchPendingGrants]);

  // Initial fetch on mount (only if authenticated)
  useEffect(() => {
    if (authenticatedUser) {
      fetchPendingGrants();
    }
  }, [fetchPendingGrants, authenticatedUser]);

  return {
    pendingGrants, // List of pending file share grant objects
    refreshPendingGrants, // Function to refresh the pending grants list
    removePendingGrant, // Function to remove a specific grant from the list
  };
}

export default usePendingFileShareRequests;
